import {
  Button,
  Grid,
  LinearProgress,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector,
  useDialogVisibilityHook
} from 'src/hooks';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
// adding App Insights hook import for telemetry logging
import { useAppInsights } from 'src/hooks/useAppInsights';
import {
  getDefaultOptionsForFormFields,
  transformDynamicFormValueToFilterPayload
} from 'src/components/filters/utils/filter-form-utils';
import { IFilterObj } from 'src/components/filters';
import { getDataForSimulatedRows, getDataForSimulatedRowsWithBaseline } from 'src/components/collapsible-table/table-data-prep';
import { getTableColumnData } from 'src/components/collapsible-table/table-column-prep';
import useMatomoFunction from 'src/hooks/use-matomo-function.hook';
import {
  toggleKPISelection,
  saveScenarioDetails,
  updateScenarioDetails,
  getPricingSimulationData,
  savePricingSimulationData,
  handleSimulationFlagValue,
  resetVisibleColumns,
  handleValueOfSavedScenario,
  resetPricingSimulationData,
  setColumnsMapping,
  setKPI2ColumnsMapping,
  setAllCollapsableColumns
} from '../store/pricing-simulator-slice';
import KPISelection from '../types/kpi-selection.enum';
import SimulationTableContainer from './simulation-table-container/simulation-table-container';
import SaveScenarioDialog from './save-scenario-dialog/save-scenario-dialog';
import OverrideNameDialog from './save-scenario-dialog/override-name-dialog/override-name-dialog';
import SimulatorFooter from '../../components/simulation-footer/simulator-footer';
import ScenarioCreateSuccessDialog from '../../home/components/scenario-create-success/scenario-create-success-dialog';
import DrawerBoxContainer from './comment-box-container/drawer-box-container';
import SaveAsContainer from '../../components/save-as-container/save-as-container';
import TopLineInputsDialog from './topline-inputs-modal/topline-inputs-modal';
import useModulePreRequisites from '../../common/hooks/use-module-pre-requisites.hook';
import MODULE_TO_PAGE_MAP from '../../home/types/simulation-module-to-page-map';
import {
  FiltersWrapper,
  setGlobalFilterFieldOptions,
  setGlobalFilterValues
} from '../../common/features';
import { handlePriceThresholdData, setTableMetaData } from '../../common/features/table/store/table-slice';
import getOutputTableColumn from '../../common/features/table/components/output-column-prep';
import { TableComponent } from '../types/generic-table.model';
import { MatomoEventTrackMapObject } from '../../constants/constant';
import OnNextClickHandler from '../../common/utils/simulator-page-next-button';
import {
  createMapping,
  createMappingKPI2,
  getExternalRowData
} from '../../common/features/table/columns-prep';
import { AlertDialog, DialogResult } from '../../../../../../src/components/analytics/alert-dialog/index';



export interface ScenarioInfo {
  name: string;
  description: string;
  duplicateName: boolean;
}

export default function PricingSimulator() {
  const appDispatch = useAppDispatch();

  // adding App Insights hook initialization for telemetry logging
  const { trackEvent, trackException } = useAppInsights();

  // moving moduleId, sourceId, scenarioId to top to avoid "used before defined" errors
  const { sourceId, moduleId, scenarioId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.PRICING
  });

  // moving currentScenarioId and navigate to top to avoid "used before defined" errors
  const { id: currentScenarioId } = useParams();
  const navigate = useNavigate();

  const {
    kpiSelection,
    pricingSimulationData,
    fetchingPricingSimulationData,
    toggleData,
    isSimulating,
    isScenarioSaving,
    isScenarioSavedSuccessfully,
    enableSimulation,
    fetchingLPData,
    visibleColumns,
    activeHeader,
    lastSimulatedSnapshot,
  } = useAppSelector((state) => state.pricingSimulator);

  const { tableMetadaData, pricingAction1Value, pricingAction2Value } = useAppSelector((state) => state.table);

  const {
    globalFilterFormFields,
    globalFilterValue,
    globalFilterCombinations,
    globalFilterInitialValues
  } = useAppSelector((state) => state.filters);

  const { callFunction } = useMatomoFunction();
  const getPageName = () => {
    const page_title = window.location.href.split('/').at(-1);
    return page_title;
  };

  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  const dialogMessage = "You have made changes on this page that haven't been simulated yet. Please click 'Simulate' to apply \nthem. If you proceed to the next module without simulating, these changes will be discarded. Are you \nsure you want to continue?"
  const dialogTitle = ""
  const { pricing: pricingAccessConfig } = useAppSelector(
    (state) => state.simulatorHome.moduleAccessMap
  );

  useEffect(() => {
    callFunction(
      MatomoEventTrackMapObject.track_page_view.event_key,
      getPageName()
    );

    // adding telemetry log for page load with business context
    trackEvent('Pricing_PageLoaded', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || ''),
      hasSavedScenario: !!currentScenarioId,
      accessLevel: pricingAccessConfig?.save ? 'full' : 'read-only',
      kpiSelection
    });
  }, []);

  useEffect(() => {
    appDispatch(setTableMetaData({ tableData: pricingSimulationData }));
  }, [pricingSimulationData]);

  const { isDisplayComments } = useAppSelector((state) => state.drawerManager);
  const [isScenarioSuccessDialogOpen, setIsScenarioSuccessDialogOpen] =
    useState(false);
  const [externalReset, setExternalReset] = useState(false);
  const [isFilterValueChanged, setIsFilterValueChanged] = useState(false);
  const handleScenarioCreateSuccessDialogClose = () => {
    setIsScenarioSuccessDialogOpen(false);
  };

  const [scenarioSimulationSavingInfo, setScenarioSimulationSavingInfo] =
    useState({
      isDialogOpen: false,
      scenario: null,
      message: null
    });

  const [scenarioSimulationInfo, setScenarioSimulationInfo] = useState({
    isDialogOpen: false,
    scenario: null,
    message: null
  });

  const [open, setOpen] = useState(false);
  const [openOverrideDialog, setOverrideDialogValue] = useState(false);
  const [isOpenSaveScenarioModal, setIsOpenSaveScenarioModal] = useState(false);
  const {
    isDialogOpen: isTopLineInputDialogOpen,
    handleDialogVisibilityToggle
  } = useDialogVisibilityHook();
  const { dynamicBreadcrumbs } = useAppSelector((state) => state.simulatorHome);
  const [scenarioData, setScenarioData] = useState({});
  const [extData, setExtData] = useState<any>([]);
  const [retailerExtData, setRetailerExtData] = useState([]);
  const [inputKPICols, setInputKPICols] = useState([]);
  const [retailerInputCols, setRetailerInputCols] = useState([]);
  const [resultKPICols, setResultKPICols] = useState([]);
  const [retailerResultCols, setRetailerResultCols] = useState([]);
  const [responseHeader, setResponseHeader] = useState([]);
  const [responseOutputToggle, setResponseOutputToggle] = useState({});
  const [responseFooterRow, setResponseFooterRow] = useState([]);

  const { isFetchingPreRequisites } = useModulePreRequisites({
    module: MODULE_TO_PAGE_MAP.INNOVATION
  });

  const [pendingNavigation, setPendingNavigation] = useState<{
    type: 'next' | 'breadcrumb';
    target?: string;
  } | null>(null);


  const checkUnsavedChanges = (compareAgainstSnapshot = false) => {
    const result = getChangedInputRows(compareAgainstSnapshot);
    return result.isChanged;
  };

  const handleConfirmationDialogClose = (result: DialogResult) => {
    if (result === DialogResult.YES && pendingNavigation) {
      if (pendingNavigation.type === 'next') {
        OnNextClickHandler(
          dynamicBreadcrumbs,
          MODULE_TO_PAGE_MAP.PRICING,
          currentScenarioId,
          navigate
        );
      } else if (pendingNavigation.type === 'breadcrumb' && pendingNavigation.target) {
        navigate(pendingNavigation.target);
      }
    }
    setConfirmationDialogOpen(false);
    setPendingNavigation(null);
  };

  const handleClickOpen = () => {
    // adding telemetry log for scenario save initiation
    trackEvent('Pricing_ScenarioSaveInitiated', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || ''),
      hasSimulated: !!lastSimulatedSnapshot,
      kpiSelection
    });

    appDispatch(
      savePricingSimulationData({
        sourceId,
        moduleId,
        scenarioId
      })
    );
  };

  useEffect(() => {
    setTimeout(() => {
      if (isSimulating) {
        setScenarioSimulationInfo({
          isDialogOpen: true,
          scenario: null,
          message: 'Simulating'
        });
      } else {
        setScenarioSimulationInfo({
          ...scenarioSimulationInfo,
          isDialogOpen: false
        });
      }
    }, 500);
  }, [isSimulating]);

  const getChangedInputRows = (compareAgainstSnapshot = false) => {
    // Only compare against snapshot if explicitly requested (Next/Breadcrumb)
    // AND a snapshot actually exists
    if (compareAgainstSnapshot && lastSimulatedSnapshot) {
      const simulatedData = getDataForSimulatedRowsWithBaseline(
        tableMetadaData,
        lastSimulatedSnapshot
      );
      return {
        isChanged: simulatedData.inputs.length > 0,
        row: simulatedData
      };
    }

    // Default behavior: compare against default values (for Simulate button, etc.)
    const simulatedData = getDataForSimulatedRows(tableMetadaData);
    return {
      isChanged: simulatedData.inputs.length > 0 || simulatedData.data.length > 0,
      row: simulatedData
    };
  };

  useEffect(() => {
    setTimeout(() => {
      if (isScenarioSavedSuccessfully) {
        setScenarioSimulationSavingInfo({
          ...scenarioSimulationSavingInfo,
          isDialogOpen: false
        });
        setIsScenarioSuccessDialogOpen(true);
        appDispatch(handleValueOfSavedScenario());
      }
    }, 500);
  }, [isScenarioSavedSuccessfully]);

  useEffect(() => {
    setTimeout(() => {
      if (isScenarioSaving) {
        setScenarioSimulationSavingInfo({
          isDialogOpen: true,
          scenario: null,
          message: 'Saving'
        });
      }
    }, 500);
  }, [isScenarioSaving]);

  const handleClose = (value: ScenarioInfo) => {
    setOpen(false);
    setIsOpenSaveScenarioModal(false);
    if (value.duplicateName === true) {
      setScenarioData(value);
      setOverrideDialogValue(true);
    } else {
      appDispatch(saveScenarioDetails(value));
    }
  };

  const handleCloseDialog = () => {
    setOverrideDialogValue(false);
  };

  const overrideSaveScenario = () => {
    setOverrideDialogValue(false);
    appDispatch(updateScenarioDetails(scenarioData));
  };

  const [columns, setColumns] = useState([]);

  const [retailerColumns, setRetailerColumns] = useState([]);

  const getColumnsData = (tableResData: any) => {
    const {
      ownKPIResultCols,
      ownKPIInputCols,
      retailerKPIInputCols,
      retailerKPIResultCols,
      response,
      priceThresholdData,
      collapsableColumns
    } = tableResData;
    const tempVar = getExternalRowData([...ownKPIInputCols, ...ownKPIResultCols])
    setExtData(tempVar);
    const tempVar2 = getExternalRowData([...retailerKPIInputCols, ...retailerKPIResultCols])
    setRetailerExtData(tempVar2);
    setInputKPICols(ownKPIInputCols);
    setRetailerInputCols(retailerKPIInputCols);
    setResultKPICols(ownKPIResultCols);
    setRetailerResultCols(retailerKPIResultCols);
    setResponseHeader(response.header);
    setResponseOutputToggle(response.output_toggle);
    setResponseFooterRow(response.footer.row);
    appDispatch(setTableMetaData({ tableData: response }));
    appDispatch(handlePriceThresholdData(priceThresholdData));
    appDispatch(setAllCollapsableColumns(collapsableColumns));
    const kpi2ColumnMapping = createMappingKPI2(response.header);
    appDispatch(setKPI2ColumnsMapping(kpi2ColumnMapping));
    const tablecolumnMapping = createMapping(response.header);
    appDispatch(setColumnsMapping(tablecolumnMapping));
    setRetailerColumns([
      ...getTableColumnData(
        retailerKPIInputCols,
        TableComponent.PRICING,
        response.header,
        [],
        !pricingAccessConfig.provide_inputs,
        moduleId,
        sourceId,
        scenarioId,
        visibleColumns,
        pricingAction1Value != 0 ? pricingAction1Value : undefined,
        activeHeader
      ),
      ...getOutputTableColumn(
        retailerKPIResultCols,
        response.output_toggle,
        response.footer.row,
        {},
        TableComponent.PRICING,
        [],
        visibleColumns['Sim Results']
      )
    ]);
    setColumns([
      ...getTableColumnData(
        ownKPIInputCols,
        TableComponent.PRICING,
        response.header,
        [],
        !pricingAccessConfig.provide_inputs,
        moduleId,
        sourceId,
        scenarioId,
        visibleColumns,
        pricingAction1Value != 0 ? pricingAction1Value : undefined,
        activeHeader
      ),
      ...getOutputTableColumn(
        ownKPIResultCols,
        response.output_toggle,
        response.footer.row,
        {},
        TableComponent.PRICING,
        [],
        visibleColumns['Sim Results']
      )
    ]);
  };

  useEffect(() => {
    setRetailerColumns([
      ...getTableColumnData(
        retailerInputCols,
        TableComponent.PRICING,
        responseHeader,
        [],
        !pricingAccessConfig.provide_inputs,
        moduleId,
        sourceId,
        scenarioId,
        visibleColumns,
        pricingAction1Value != 0 ? pricingAction1Value : undefined,
        activeHeader
      ),
      ...getOutputTableColumn(
        retailerResultCols,
        responseOutputToggle,
        responseFooterRow,
        {},
        TableComponent.PRICING,
        [],
        visibleColumns['Sim Results']
      )
    ]);
    setColumns([
      ...getTableColumnData(
        inputKPICols,
        TableComponent.PRICING,
        responseHeader,
        [],
        !pricingAccessConfig.provide_inputs,
        moduleId,
        sourceId,
        scenarioId,
        visibleColumns,
        pricingAction1Value != 0 ? pricingAction1Value : undefined,
        activeHeader
      ),
      ...getOutputTableColumn(
        resultKPICols,
        responseOutputToggle,
        responseFooterRow,
        {},
        TableComponent.PRICING,
        [],
        visibleColumns['Sim Results']
      )
    ]);
  }, [pricingAction1Value, pricingAction2Value, visibleColumns]);

  /**
   * Handler for apply button click
   */
  const handleFilterApply = (filters: IFilterObj[]) => {
    // adding telemetry log for filter apply action
    trackEvent('Pricing_FiltersApplied', {
      filterCount: filters?.length || 0,
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || '')
    });

    appDispatch(
      getPricingSimulationData({
        dataInput: {
          filter_inputs: filters,
          row: []
        },
        sourceId,
        moduleId,
        scenarioId
      })
    ).then((res: any) => {
      getColumnsData(res.payload);
    });
  };

  // the handler function for the next button functionality

  const handleBreadcrumbClick = (routeURL: string): boolean => {
    // If same module, allow navigation without dialog
    if (routeURL === window.location.pathname) {
      return true;
    }
    if (!checkUnsavedChanges(true)) {
      return true;
    }
    setPendingNavigation({ type: 'breadcrumb', target: routeURL });
    setConfirmationDialogOpen(true);
    return false;
  };

  const onNextClick = () => {
    if (!checkUnsavedChanges(true)) {
      OnNextClickHandler(
        dynamicBreadcrumbs,
        MODULE_TO_PAGE_MAP.PRICING,
        currentScenarioId,
        navigate
      );
      return;
    }
    setPendingNavigation({ type: 'next' });
    setConfirmationDialogOpen(true);
  };


  /**
   * Handler for simulate button click
   */
  const handleSimulationClick = () => {
    // adding telemetry log for simulation initiation with context
    trackEvent('Pricing_SimulationInitiated', {
      hasFilters: Object.keys(globalFilterValue || {}).length > 0,
      scenarioId: String(scenarioId || 'new'),
      sourcePage: window.location.pathname,
      isSimulating,
      kpiSelection,
      visibleColumnsCount: Object.values(visibleColumns).filter(Boolean).length
    });

    callFunction(
      MatomoEventTrackMapObject.track_simulate_button_click.event_key,
      getPageName()
    );

    const result = getChangedInputRows();
    if (!result.isChanged) {
      // adding telemetry log for skipped simulation due to no changes
      trackEvent('Pricing_SimulationSkipped', {
        reason: 'no_changes_detected'
      });
      return;
    }
    appDispatch(handleSimulationFlagValue());
    appDispatch(resetVisibleColumns());
    const filters = transformDynamicFormValueToFilterPayload({
      formValue: globalFilterValue,
      formFields: globalFilterFormFields,
      combinations: globalFilterCombinations
    });
    const dataPoints = {
      filter_inputs: filters,
      row: result.row
    };
    appDispatch(
      getPricingSimulationData({
        dataInput: dataPoints,
        sourceId,
        moduleId,
        scenarioId
      })
    ).then((res: any) => {
      // adding telemetry log for simulation completion or error
      if (res?.meta?.requestStatus === 'rejected') {
        trackException(new Error(res['error']?.message || 'Unknown error'), {
          context: 'Pricing_Simulation',
          scenarioId: String(scenarioId || 'new'),
          moduleId: String(moduleId || '')
        });
      } else {
        const payload = res.payload as { tableData?: { row?: any[] } };
        trackEvent('Pricing_SimulationCompleted', {
          rowCount: payload?.tableData?.row?.length || 0,
          scenarioId: String(scenarioId || 'new'),
          kpiSelection
        });
      }
      getColumnsData(res.payload);
    });
  };

  /**
   * Handler for simulation reset
   */
  const handleSimulationReset = () => {
    // adding telemetry log for simulation reset action
    trackEvent('Pricing_SimulationReset', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      hasUnsavedChanges: getChangedInputRows().isChanged,
      kpiSelection
    });

    setExternalReset(true);
    setTimeout(() => {
      setExternalReset(false);
    }, 2000);
    appDispatch(setGlobalFilterValues(globalFilterInitialValues));
    appDispatch(
      setGlobalFilterFieldOptions(
        getDefaultOptionsForFormFields(
          globalFilterFormFields,
          globalFilterCombinations,
          globalFilterInitialValues
        )
      )
    );
    appDispatch(
      resetPricingSimulationData({
        sourceId,
        moduleId,
        scenarioId
      })
    ).then((res: any) => {
      getColumnsData(res.payload);
    });
    appDispatch(resetVisibleColumns());
  };

  // adding telemetry log for KPI selection toggle
  const handleKPISelectionChange = (_event: React.MouseEvent<HTMLElement>, newKPI: KPISelection) => {
    trackEvent('Pricing_KPISelectionChanged', {
      previousKPI: kpiSelection,
      newKPI,
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || '')
    });
    appDispatch(toggleKPISelection(newKPI));
  };

  // adding telemetry log for topline inputs dialog open
  const handleTopLineInputsOpen = () => {
    trackEvent('Pricing_TopLineInputsDialogOpened', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || '')
    });
    handleDialogVisibilityToggle();
  };

  if (isFetchingPreRequisites) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      </Grid>
    );
  }
  return (
    <>
      <Grid sx={{ padding: (theme) => theme.spacing(1, 2) }}>
        <FiltersWrapper
          accessConfig={pricingAccessConfig}
          externalReset={externalReset}
          moduleId={moduleId}
          onValueApply={handleFilterApply}
          onFilterFieldsFetched={handleFilterApply}
          scenarioId={scenarioId}
          setIsFilterValueChanged={(value) => {
            setIsFilterValueChanged(value);
          }}
          sourceId={sourceId}
        />
        {fetchingPricingSimulationData && (
          <Grid container my={1} justifyContent='space-between'>
            <Grid item>
              <Grid container spacing={2}>
                <Grid alignSelf='center' item>
                  <Skeleton animation='wave' sx={{ width: 100, height: 50 }} />
                </Grid>
                <Grid
                  alignSelf='center'
                  item
                  style={{ paddingLeft: '15px', fontSize: '12px' }}
                >
                  <Skeleton animation='wave' sx={{ width: 100, height: 50 }} />
                </Grid>
                <Grid item xs='auto' alignSelf='center'>
                  <div
                    style={{
                      height: '40px',
                      width: '1px'
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              justifyContent='flex-end'
              alignContent='end'
              spacing={2}
              item
              xs='auto'
            >
              <Grid item>
                <Skeleton animation='wave' sx={{ width: 200, height: 50 }} />
              </Grid>
              <Grid item>
                <Skeleton animation='wave' sx={{ width: 100, height: 50 }} />
              </Grid>
              <Grid item>
                <Skeleton animation='wave' sx={{ width: 100, height: 50 }} />
              </Grid>
            </Grid>
          </Grid>
        )}
        {!fetchingPricingSimulationData && (
          <Grid container my={1} justifyContent='space-between'>
            <Grid item>
              <Grid container spacing={2}>
                <Grid alignSelf='center' item>
                  <ToggleButtonGroup
                    size='small'
                    color='primary'
                    value={kpiSelection}
                    exclusive
                    onChange={handleKPISelectionChange}
                  >
                    {/* eslint-disable arrow-body-style */}
                    {toggleData.map((kpi) => {
                      return (
                        <ToggleButton key={kpi.name} value={kpi.name}>
                          {kpi.name}
                        </ToggleButton>
                      );
                    })}
                  </ToggleButtonGroup>
                </Grid>

                <Grid
                  alignSelf='center'
                  item
                  style={{ paddingLeft: '15px', fontSize: '12px' }}
                >
                  <Button
                    variant='outlined'
                    style={{ fontSize: '12px' }}
                    disabled={!pricingAccessConfig?.provide_top_line_inputs}
                    onClick={handleTopLineInputsOpen}
                  >
                    Add Topline Inputs
                  </Button>
                </Grid>
                <Grid item xs='auto' alignSelf='center'>
                  <div
                    style={{
                      height: '40px',
                      width: '1px'
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              justifyContent='flex-end'
              alignContent='end'
              spacing={2}
              item
              xs='auto'
            >
              {pricingSimulationData.legend.metadata &&
                pricingSimulationData.legend.metadata.length > 0 &&
                pricingSimulationData.legend.metadata.map((legendData) => {
                  return (
                    <Grid item key={legendData.pk_id}>
                      <div
                        className='sku-legend-div'
                        style={{ margin: 'auto' }}
                      >
                        <div
                          className='sku-legend-box'
                          style={{
                            background: `${legendData.color}`,
                            marginTop: '4px'
                          }}
                        />
                        <div
                          className='legend-title'
                          style={{ marginTop: '7px' }}
                        >
                          {legendData.label}{' '}
                        </div>
                      </div>
                    </Grid>
                  );
                })}
              <Grid item>
                <SaveAsContainer disabled={!pricingAccessConfig?.save_as} />
              </Grid>
              <Grid item>
                <div>
                  <Button
                    variant='contained'
                    onClick={handleClickOpen}
                    disabled={!enableSimulation || !pricingAccessConfig?.save}
                  >
                    Save
                  </Button>
                  {isOpenSaveScenarioModal && (
                    <SaveScenarioDialog open={open} onClose={handleClose} />
                  )}
                  <OverrideNameDialog
                    open={openOverrideDialog}
                    onClose={handleCloseDialog}
                    updateScenarioData={overrideSaveScenario}
                  />
                </div>
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid container>
          <Grid item xs={12}>
            <div
              style={{ cursor: 'pointer' }}
            >
              <SimulationTableContainer
                columns={columns}
                kpiSelection={kpiSelection}
                retailerColumns={retailerColumns}
                externalColumnData={Array.isArray(extData) ? extData : []}
                externalRetailerData={Array.isArray(retailerExtData) ? retailerExtData : []}
                visibleColumns={visibleColumns}
                activeHeader={activeHeader}
              />
            </div>
          </Grid>
        </Grid>
        {isScenarioSuccessDialogOpen ? (
          <ScenarioCreateSuccessDialog
            imagePath='/static/images/success.svg'
            isOpen={isScenarioSuccessDialogOpen}
            onClose={handleScenarioCreateSuccessDialogClose}
            message='Scenario has been successfully Saved'
          />
        ) : null}

        <ScenarioCreateSuccessDialog
          isOpen={scenarioSimulationInfo.isDialogOpen}
          imagePath='/static/images/file-text.svg'
          message={scenarioSimulationInfo.message}
        />
        <ScenarioCreateSuccessDialog
          isOpen={scenarioSimulationSavingInfo.isDialogOpen}
          imagePath='/static/images/upload-cloud.svg'
          message={scenarioSimulationSavingInfo.message}
        />
      </Grid>
      {isDisplayComments ? <DrawerBoxContainer /> : null}
      <SimulatorFooter
        simulationClickHandler={handleSimulationClick}
        resetSimulationHandler={handleSimulationReset}
        isSimulationDisabled={
          fetchingLPData ||
          !pricingAccessConfig.simulate ||
          isFilterValueChanged
        }
        isResetSimulationDisabled={
          fetchingLPData || !pricingAccessConfig.reset_simulation
        }
        isNextDisabled={fetchingLPData}
        nextClickHandler={onNextClick}
        isButtonSectionVisible
        onBreadcrumbClick={handleBreadcrumbClick}

      />
      {isTopLineInputDialogOpen ? (
        <TopLineInputsDialog
          isOpen={isTopLineInputDialogOpen}
          onClose={() => {
            handleDialogVisibilityToggle();
          }}
        />
      ) : null}
      <AlertDialog
        closeHandler={handleConfirmationDialogClose}
        isDialogOpen={isConfirmationDialogOpen}
        message={dialogMessage}
        title={dialogTitle}
      />
    </>
  );
}